import { Client } from "@xmpp/client"
import XmppPubSubApi from "../api/xmppPubSubApi"
import { captureError } from "../ErrorHandlers"
import { retry } from "../utils"
import { communicatorJid, wizardJid } from "../jidUtils"
import { USER_CHAT_NS } from "@/lib/accountChatsParser"
import {
  subscribeToPinnedMessages,
  retrieveAllPinnedMessages,
} from "../api/xeps/PinnedMessages"
import { subscribeToTachos, retrieveAllTachos } from "../api/xeps/Tachos"
import {
  subscribeToUserActivities,
  retrieveAll as retrieveAllActivities,
} from "../api/xeps/UserActivity"
import {
  subscribeToReferenceRouteStatuses,
  retrieveAllReferenceRouteStatuses,
} from "../api/xeps/ReferenceRouteStatus"
import {
  subscribeToRouteAlertConfigs,
  retrieveAllRouteAlertConfigs,
} from "../api/xeps/RouteAlertConfigs"
import { subscribeToChatsSettings } from "@/api/xeps/ChatsSettings"
import {
  subscribeToLocations,
  retrieveAllDriverLocations,
} from "@/api/xeps/Locations"
import {
  subscribeToTrailerLocations,
  retrieveAllTrailerLocations,
} from "@/api/xeps/TrailerLocations"
import { subscribeToRoutes, retrieveAllDriverRoutes } from "@/api/xeps/Routes"
import {
  subscribeToGpsConfig,
  retrieveAllGpsConfig,
} from "@/api/xeps/GpsConfig"
import {
  subscribeToUsersVCards,
  retrieveAllUsersVCards,
} from "@/api/xeps/Users"
import { subscribeToCars, retrieveAllCars } from "@/api/xeps/Cars"
import {
  subscribeToDeliveryOrderExports,
  retrieveAllDeliveryOrderExportStatuses,
} from "@/api/xeps/DeliveryOrderExports"
import { isSltExportEnabledVhost } from "@/lib/sltExportHostRules"
import {
  retrieveAllWorkflows,
  subscribeToWorkflows,
} from "@/api/xeps/OpenclawWorkflows"
import {
  subscribeToServiceConfig,
  retrieveAllServiceConfigs,
} from "@/api/xeps/ServiceConfig"
import { retrieveAllBookmarks } from "../api/xeps/PepNativeBookmarks"
import { batchUpsertTachos } from "../reducers/tachosSlice"
import { batchUpsertDeliveryOrderExportStatuses } from "../reducers/deliveryOrderExportStatusesSlice"
import { batchUpsertActivities } from "../reducers/activitySlice"
import { batchUpsertReferenceRouteStatuses } from "../reducers/referenceRouteStatusesSlice"
import { batchUpsertLocation } from "@/reducers/locationsSlice"
import { batchUpsertTrailerLocations } from "@/reducers/trailerLocationsSlice"
import {
  batchUpsertRoute2g,
  batchUpdateRouteAlertConfigs,
} from "@/reducers/routes2gSlice"
import { batchUpsertGpsConfigs } from "@/reducers/gpsConfigSlice"
import { setAllCars } from "@/reducers/carsSlice"
import { setAllWorkflows } from "@/reducers/openclawWorkflowsSlice"
import {
  setGpsServiceConfig,
  setIdentityConfig,
} from "@/reducers/serviceConfigSlice"
import { upsertContact as upsertCommunicatorContact } from "../reducers/communicatorContactsSlice"
import { upsertProfile } from "../reducers/profilesSlice"
import { batchPinMessages, batchUnpinMessages } from "../reducers/pinnedSlice"
import { allPinnedMessagesSelector } from "../reducers/pinnedSliceSelectors"
import {
  markGpsConfigsFetched,
  markDriverLocationsFetched,
} from "../reducers/roomSyncStatusSlice"
import { RootState } from "../reducers/store"

export const FULL_ROOM_SYNC_DONE = "FullRoomSyncDone"

// --- Subscriptions ---

const subscribeToBookmarks = async (client: Client, jid: string) => {
  try {
    await XmppPubSubApi.subscribe(client, jid, "urn:xmpp:bookmarks:1")
  } catch (error: any) {
    if (error.condition !== "item-not-found") {
      captureError(error, {
        origin: "InitialLoad",
        extra: { message: "subscribeToBookmarks", jid },
      })
    }
  }
}

const subscribeToAccountChats = async (client: Client) => {
  const jid = wizardJid(client.jid!)
  try {
    await XmppPubSubApi.subscribe(client, jid, "urn:slickshift:account:chats:0")
  } catch (error: any) {
    if (error.condition !== "item-not-found") {
      captureError(error, {
        origin: "InitialLoad",
        extra: { message: "subscribeToAccountChats" },
      })
    }
  }
}

const subscribeToUserChats = async (client: Client) => {
  const jid = wizardJid(client.jid!)
  try {
    await XmppPubSubApi.subscribe(client, jid, USER_CHAT_NS)
  } catch (error: any) {
    if (error.condition !== "item-not-found") {
      captureError(error, {
        origin: "InitialLoad",
        extra: { message: "subscribeToUserChats" },
      })
    }
  }
}

// --- Fetches ---

const fetchCommunicatorContacts = async () => {
  const communicatorBookmarks = await retrieveAllBookmarks(
    window.XMPP!,
    `communicator@${window.XMPP!.jid?.domain}`,
  )
  for (const bookmark of communicatorBookmarks) {
    window.store.dispatch(
      upsertCommunicatorContact({
        contact: { jid: bookmark.jid, name: bookmark.name ?? "" },
      }),
    )
  }
}

const fetchAllUsersVCards = async () => {
  // Retry up to 3 times with back-off so a transient failure doesn't leave
  // stale persisted profiles as the only source of truth for sender identity.
  const usersVCards = await retry(retrieveAllUsersVCards, [window.XMPP!], {
    maxAttempts: 3,
    backoff: true,
  })
  for (const vCard of usersVCards) {
    if (vCard) {
      window.store.dispatch(upsertProfile({ profile: vCard }))
    }
  }
}

const fetchAllPinnedMessages = async () => {
  const state = window.store.getState() as RootState
  const currentPinnedMessages = allPinnedMessagesSelector(state)
  const fetchedPinnedMessages = await retrieveAllPinnedMessages(window.XMPP!)

  const fetchedMessageItemIds = new Set(
    fetchedPinnedMessages.map((m) => m.itemId),
  )
  const currentMessageItemIds = new Set(
    Object.values(currentPinnedMessages).map((m) => m.itemId),
  )
  const pinnedMessageItemIdsToUnpin = Array.from(currentMessageItemIds).filter(
    (itemId) => !fetchedMessageItemIds.has(itemId),
  )

  window.store.dispatch(
    batchUnpinMessages({ itemIds: pinnedMessageItemIdsToUnpin }),
  )
  window.store.dispatch(batchPinMessages({ messages: fetchedPinnedMessages }))
}

const fetchAllTachos = async () => {
  const fetchedTachos = await retrieveAllTachos(window.XMPP!)
  window.store.dispatch(batchUpsertTachos({ tachos: fetchedTachos }))
}

export const safeFetchAllDriverLocations = async (): Promise<void> => {
  try {
    const fetchedDriverLocations = await retry(
      retrieveAllDriverLocations,
      [window.XMPP!],
      {
        maxAttempts: 3,
        backoff: true,
      },
    )
    window.store.dispatch(
      batchUpsertLocation({ driversLocations: fetchedDriverLocations }),
    )
    window.store.dispatch(markDriverLocationsFetched())
  } catch (e) {
    const message = (e as Error)?.message ?? String(e)
    window.analytics.track("FetchAllDriverLocationsFailed", { message })
    captureError(e, {
      origin: "InitialLoad",
      extra: { message: "safeFetchAllDriverLocations failed" },
    })
  }
}

const fetchAllDriverRoutes = async () => {
  const fetchedRoutes = await retrieveAllDriverRoutes(window.XMPP!)
  window.store.dispatch(batchUpsertRoute2g({ routes: fetchedRoutes }))
}

const fetchAllGpsConfigs = async () => {
  const fetchedGpsConfigs = await retrieveAllGpsConfig(window.XMPP!)
  window.store.dispatch(
    batchUpsertGpsConfigs({ gpsConfigs: fetchedGpsConfigs }),
  )
  window.store.dispatch(markGpsConfigsFetched())
}

const fetchAllReferenceRouteStatuses = async () => {
  const fetchedReferenceRouteStatuses = await retrieveAllReferenceRouteStatuses(
    window.XMPP!,
  )
  window.store.dispatch(
    batchUpsertReferenceRouteStatuses({
      referenceRouteStatuses: fetchedReferenceRouteStatuses,
    }),
  )
}

const fetchAllRouteAlertConfigs = async () => {
  const fetchedConfigs = await retrieveAllRouteAlertConfigs(window.XMPP!)
  const configs = fetchedConfigs.map(({ jid, ...config }) => ({ jid, config }))
  window.store.dispatch(batchUpdateRouteAlertConfigs({ configs }))
}

const fetchAllActivities = async () => {
  const activities = await retrieveAllActivities(window.XMPP!)
  window.store.dispatch(batchUpsertActivities({ activities }))
}

const fetchAllCars = async () => {
  const cars = await retrieveAllCars(window.XMPP!)
  window.store.dispatch(setAllCars({ cars }))
}

const fetchAllDeliveryOrderExportStatuses = async () => {
  const exportStatuses = await retrieveAllDeliveryOrderExportStatuses(
    window.XMPP!,
  )

  window.store.dispatch(
    batchUpsertDeliveryOrderExportStatuses({ exportStatuses }),
  )
}

const fetchAllServiceConfigs = async () => {
  const configs = await retrieveAllServiceConfigs(window.XMPP!)
  if (configs.gps) window.store.dispatch(setGpsServiceConfig(configs.gps))
  if (configs.identity)
    window.store.dispatch(setIdentityConfig(configs.identity))
}

const fetchAllWorkflows = async () => {
  await subscribeToWorkflows(window.XMPP!)
  const workflows = await retrieveAllWorkflows(window.XMPP!)
  window.store.dispatch(setAllWorkflows({ workflows }))
}

const fetchAllTrailerLocations = async () => {
  const trailerLocations = await retrieveAllTrailerLocations(window.XMPP!)
  window.store.dispatch(batchUpsertTrailerLocations({ trailerLocations }))
}

// --- Orchestration ---

const runFetch = async (
  name: string,
  fn: () => Promise<void>,
): Promise<void> => {
  try {
    await fn()
  } catch (e) {
    captureError(e, {
      origin: "InitialLoad",
      extra: { message: `${name} failed` },
    })
  }
}

const syncAllPubSubs = async (client: Client) => {
  await subscribeToBookmarks(client, communicatorJid(client.jid!))
  await subscribeToLocations(client)
  await subscribeToTrailerLocations(client)
  await subscribeToRoutes(client)
  await subscribeToPinnedMessages(client)
  await subscribeToAccountChats(client)
  await subscribeToUserChats(client)
  await subscribeToTachos(client)
  await subscribeToUserActivities(client)
  await subscribeToReferenceRouteStatuses(client)
  await subscribeToRouteAlertConfigs(client)
  await subscribeToChatsSettings(client)
  await subscribeToGpsConfig(client)
  await subscribeToServiceConfig(client)
  await subscribeToUsersVCards(client)
  await subscribeToCars(client)

  const isSltExportVhost = isSltExportEnabledVhost(client.jid?.domain)
  if (isSltExportVhost) {
    await subscribeToDeliveryOrderExports(client)
  }

  await runFetch("fetchCommunicatorContacts", fetchCommunicatorContacts)
  await runFetch("fetchAllUsersVCards", fetchAllUsersVCards)
  await runFetch("fetchAllPinnedMessages", fetchAllPinnedMessages)
  await runFetch("fetchAllGpsConfigs", fetchAllGpsConfigs)
  await runFetch("fetchAllTachos", fetchAllTachos)
  await safeFetchAllDriverLocations()
  await runFetch("fetchAllTrailerLocations", fetchAllTrailerLocations)
  await runFetch("fetchAllDriverRoutes", fetchAllDriverRoutes)
  await runFetch(
    "fetchAllReferenceRouteStatuses",
    fetchAllReferenceRouteStatuses,
  )
  await runFetch("fetchAllRouteAlertConfigs", fetchAllRouteAlertConfigs)
  await runFetch("fetchAllActivities", fetchAllActivities)
  await runFetch("fetchAllCars", fetchAllCars)
  await runFetch("fetchAllWorkflows", fetchAllWorkflows)
  await runFetch("fetchAllServiceConfigs", fetchAllServiceConfigs)
  if (isSltExportVhost) {
    await runFetch(
      "fetchAllDeliveryOrderExportStatuses",
      fetchAllDeliveryOrderExportStatuses,
    )
  }
}

export const initPubSubSync = (client: Client) => {
  let isRunning = false
  let isPending = false

  const run = async () => {
    if (isRunning) {
      isPending = true
      return
    }

    isRunning = true
    isPending = false
    console.log("[PubSubSync] Starting subscribe and fetch")
    const start = performance.now()

    try {
      await syncAllPubSubs(client)
      console.log(
        `[PubSubSync] Subscribe and fetch complete in ${Math.round(performance.now() - start)}ms`,
      )
    } catch (e) {
      captureError(e, {
        origin: "InitialLoad",
        extra: { message: "syncAllPubSubs failed" },
      })
    } finally {
      isRunning = false
      if (isPending) void run()
    }
  }

  client.on(FULL_ROOM_SYNC_DONE, () => void run())
}
